import React, {
    memo,
    MutableRefObject,
    RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { CSSTransition } from 'react-transition-group';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-use';
import { useRouter } from 'next/router';
import { twMerge } from 'tailwind-merge';
import cn from 'classnames';
import Skeleton from 'react-loading-skeleton';
import {
    CONTENT_TYPE_VALUE,
    OBJECT_TYPE,
    TIME_SET_CONTENT,
} from 'src/constants';
import { IAppPage, IContent, IContentDetail } from 'src/interface';
import { Logger, UrlUtils } from 'src/utils';
import { useViewTime } from 'src/hooks';
import { trackingPage, trackingPageDetail } from 'src/utils/tracking';
import { setIdsRead } from 'src/utils/content';
import {
    isIosSelector,
    isMiniappSelector,
    pageSelector,
} from 'src/selectors/app';
import {
    currentContentSelector,
    isFetchingSelector,
    isOpenSelector,
} from 'src/selectors/content';
import { strUserIdSelector } from 'src/selectors/auth';
import { loadAdNative } from 'src/redux/actions/ad';
import { openContent } from 'src/redux/actions/content';
import { AdDispatchActions, ContentDispatchActions } from 'src/redux/actions';
import { Element, IconButton, RouterLink } from 'src/components';
import { Loader } from '@zmedia/bm-common/components/loader';
import Scrollbars from 'src/components/scrollbar';
import { ContentPublisher } from 'src/modules/content/components/shared/ContentPublisher';
import { ContentMoreAction } from 'src/modules/content/views/components/ContentAction/more';
import FollowButton from 'src/modules/shared/components/buttons/FollowButton';
import ShareButton from 'src/modules/shared/components/buttons/ShareButton';
import { AdBanner } from 'src/modules/shared/components/ad';
import { GATracking } from 'src/modules/shared/components/GA';
import {
    ContentMain,
    ContentsHistory,
    ContentSocial,
    ContentSource,
    ContentsRelated,
    EventSoccer,
} from 'src/modules/content/views';
import { AdMastheadPopup } from './adMastheadPopup';
import { ContentDetailContext } from 'src/pages/content/detail/[id]';

import styles from './content.module.scss';
import 'react-loading-skeleton/dist/skeleton.css';

const CONTENT_SCROLL = 'content-ui-scroll';

export function ContentUI() {
    const router = useRouter();
    const contentRef: MutableRefObject<HTMLDivElement> | any =
        useRef<RefObject<HTMLDivElement>>(null);
    const nodeRef = useRef(null);
    const intervalRef: MutableRefObject<any> = useRef(1);
    const page: IAppPage = useSelector(pageSelector);
    const isOpen: boolean = useSelector(isOpenSelector);
    const isIframe = useSelector(isMiniappSelector);
    const currentContent = useRef<IContentDetail | undefined>(undefined);
    const openContentFromUrl = useRef<boolean>(false);
    const historyUrl = useRef<string[]>([]);
    const currentPage = useRef<IAppPage>(page);
    const content: IContentDetail | undefined = useSelector(
        currentContentSelector,
    );

    const dispatch = useDispatch();
    const strUserId = useSelector(strUserIdSelector);
    const isFetching: boolean = useSelector(isFetchingSelector);
    const location = useLocation();
    const isIos = useSelector(isIosSelector);
    const [animatePopup, setAnimatePopup] = useState<boolean>(true);

    const renderedKeys = useRef(new Map());

    const register = (key: string, id: number) => {
        if (!renderedKeys.current.has(key)) {
            renderedKeys.current.set(key, id);
            return true;
        }

        return renderedKeys.current.get(key) === id;
    };

    const release = (key: string, id: number) => {
        if (renderedKeys.current.get(key) === id) {
            renderedKeys.current.delete(key);
        }
    };

    const _closeContent = (replaceState = true) => {
        Logger.debug('__closeContent');
        dispatch(loadAdNative((window as any).zoneIds));
        currentContent.current = undefined;
        openContentFromUrl.current = false;
        if (historyUrl.current.length > 1) {
            history.go(-historyUrl.current.length);
        }
        historyUrl.current = [];

        setTimeout(() => {
            dispatch(ContentDispatchActions.closeContent());
            document.getElementById('layout')?.removeAttribute('style');
        }, 100);
    };

    const _openContent = (content: IContent) => {
        dispatch(openContent(content));
    };

    const handleBack = (evt: React.MouseEvent) => {
        evt.preventDefault();
        evt.stopPropagation();

        if (isIframe) {
            _closeContent();
            return;
        }
        router.back();
        historyUrl.current.pop();
    };

    const handleClose = (evt: React.MouseEvent) => {
        evt.preventDefault();
        evt.stopPropagation();
        _closeContent();
    };

    useEffect(() => {
        return () => {
            _closeContent();
        };
    }, []);

    useEffect(() => {
        const layout = document.getElementById('layout');
        if (isOpen) {
            const top =
                window.pageYOffset || document.documentElement.scrollTop;
            (window as any).scrollTopValue = top;
            setTimeout(() => {
                if (layout) {
                    layout.style.height = '100vh';
                    layout.style.overflow = 'hidden';
                }
            }, 50);
        } else {
            layout?.removeAttribute('style');
            setTimeout(() => {
                window.scrollTo(0, (window as any).scrollTopValue);
                Logger.debug(
                    'isOpen change | scrollTo',
                    (window as any).scrollTopValue,
                );
            }, 0);
        }
    }, [isOpen]);

    useEffect(() => {
        (window as any).scrollTopValue = 0;
        window.scrollTo(0, 0);
        currentPage.current = page;
        Logger.debug(
            'pageChange | page:',
            page,
            ' | location.pathname:',
            location.pathname,
        );
    }, [page]);

    useEffect(() => {
        if (content && isOpen) {
            if (!isFetching) {
                currentContent.current = content;
                if (!openContentFromUrl.current) {
                    Logger.debug(
                        'content change | pushState:',
                        content.url,
                        ' | currentPage:',
                        currentPage.current,
                    );
                    window.history.pushState(
                        { title: content.title, contentUrl: content?.url },
                        content.title,
                        content?.url,
                    );
                    historyUrl.current.push(content.url);
                }
            }
        }

        return () => {};
    }, [content, isFetching, isOpen]);

    useEffect(() => {
        Logger.debug(
            'location change | pathname',
            location.pathname,
            ' | location.trigger:',
            location.trigger,
            ' | currentPage:',
            currentPage,
            ' | currentContent:',
            currentContent.current?.title,
        );

        const pathName = location.pathname;

        if (!pathName) {
            return;
        }

        if (!UrlUtils.isContentDetailUrl(pathName)) {
            if (isOpen) {
                setAnimatePopup(false);
                _closeContent();

                setTimeout(() => {
                    if (pathName?.indexOf(currentPage.current.url) === -1) {
                        Logger.debug(
                            'location change | not same url | pathname:',
                            location.pathname,
                            '|currentPage:',
                            currentPage,
                        );
                        router.replace(pathName);
                    }
                }, 200);
            }

            return;
        }

        if (currentContent.current?.url === pathName) {
            return;
        }

        const _contentId = UrlUtils.getContentIdFromUrl(pathName);
        Logger.debug('location change |  _contentId:', _contentId);

        if (isOpen) {
            if (
                _contentId > 0 &&
                _contentId != currentContent?.current?.contentId
            ) {
                _openContent({
                    id: _contentId,
                    contentId: _contentId,
                } as any);
            }
        } else {
            if (_contentId > 0) {
                Logger.debug('location change |  _contentId2:', _contentId);
                _openContent({
                    id: _contentId,
                    contentId: _contentId,
                } as any);
            }
        }

        openContentFromUrl.current = true;

        return () => {};
    }, [location.pathname]);

    useEffect(() => {
        if (content && !isFetching) {
            if (content?.tracking) {
                (window as any)._BM.sourceTracking = content.tracking;
            }

            contentRef.current = document.getElementById(CONTENT_SCROLL);
            if (typeof contentRef.current?.scrollTo === 'function') {
                contentRef.current?.scrollTo(0, 0);
            }
            trackingPage('mobile_all');
            trackingPageDetail(content.contentId, strUserId, true);

            const { forceRedirectUrl } = content;

            if (forceRedirectUrl) {
                router.push(forceRedirectUrl);
                return;
            }
            if (typeof content.isNoAd !== 'undefined') {
                const _window: any = window;
                _window.adtimaBMConfig = content.ad.adtimaConfig;
                dispatch(AdDispatchActions.isNoAdUpdate(content.isNoAd));
                dispatch(loadAdNative(content.ad.zoneIds));
            }
        }
    }, [content, isFetching]);

    useEffect(() => {
        if (intervalRef && intervalRef.current && content) {
            intervalRef.current = setInterval(() => {
                if (currentPage.current.type === 'homepage') {
                    setIdsRead(content.contentId);
                    clearInterval(intervalRef.current);
                    dispatch(
                        ContentDispatchActions.setReadId(content.contentId),
                    );
                }
            }, TIME_SET_CONTENT);
        }
        return () => {
            clearInterval(intervalRef.current);
        };
    }, [content]);

    useEffect(() => {
        router.beforePopState(() => !isOpen);
    }, [isOpen, location.pathname]);

    const renderAdsBottom = useCallback(() => {
        if (content && content.ad?.bottoms) {
            return content.ad?.bottoms?.map((id: string) => {
                return <AdBanner elId={id} key={id} classNameContainer={id} />;
            });
        }
        return null;
    }, [content]);

    const renderContent = useCallback(() => {
        Logger.info('content:', content);

        if (!content) {
            return null;
        }

        if (content.embedUrl) {
            return <EmbedUrl url={content.embedUrl} />;
        }

        return (
            <>
                <Element className={styles['content-ui-main']}>
                    <>
                        <Element className={styles['content-wrapper']}>
                            {isFetching ? (
                                <>
                                    <Loader text={'Đang tải nội dung ...'} />
                                </>
                            ) : (
                                <>
                                    <ContentMain
                                        {...content}
                                        isFetching={isFetching}
                                        isOpenContent={true}
                                        showMoreButton={false}
                                    />
                                    <Element className="mb-[24px] empty:hidden">
                                        <ContentSocial
                                            id={-1}
                                            contentId={content.contentId}
                                            title={content.title}
                                            url={content.url}
                                        />
                                    </Element>
                                    <ContentsHistory content={content} />
                                    <ContentSource
                                        publisher={content.publisher}
                                        originalUrl={content.originalUrl}
                                        redirectUrl={content.redirectUrl}
                                        tags={content.topTags}
                                    />

                                    <EventSoccer content={content} />
                                </>
                            )}
                        </Element>
                        {!isFetching && (
                            <>
                                <ContentsRelated
                                    contentId={content.contentId as any}
                                />
                                <RouterLink
                                    to="/"
                                    className="flex justify-center"
                                >
                                    <IconButton
                                        className={cn(
                                            'bg-[var(--primary)]',
                                            'text-white text-[16px] uppercase',
                                            'h-[48px]',
                                            'px-[20px] my-[20px]',
                                            'rounded-full',
                                            'flex items-center',
                                        )}
                                        classNameIcon={cn(
                                            'text-[24px] mr-[12px]',
                                        )}
                                        title="Về trang chủ"
                                        text="Về trang chủ"
                                        icon="new-home-regular"
                                    ></IconButton>
                                </RouterLink>
                                {renderAdsBottom()}
                            </>
                        )}
                    </>
                </Element>
            </>
        );
    }, [content, isFetching]);

    const { ref } = useViewTime({
        tracking: 'page',
        contentId: content?.contentId,
        threshold: 0,
        onFinish: () => {},
    });

    // disable animate when closing content without click handleBack action in iOS
    const disableAnimation = (isIos && !animatePopup) || true;
    const timer = disableAnimation ? 0 : 300;
    const className = disableAnimation ? '' : 'bm-content-ui-animation';
    return (
        <CSSTransition
            in={isOpen}
            mountOnEnter
            unmountOnExit
            timeout={timer}
            classNames={className}
            nodeRef={nodeRef}
            onExited={() => setAnimatePopup(true)}
        >
            <Element
                domRef={ref}
                className={twMerge(styles['content-ui'], 'bm-topbar', 'top-0')}
            >
                {isOpen && (
                    <>
                        <Element
                            className={cn(styles['text-back'], {
                                [styles['is-live']]:
                                    content?.contentTypes?.includes(
                                        CONTENT_TYPE_VALUE.LIVE,
                                    ),
                                [styles['has-close']]:
                                    historyUrl.current.length > 1,
                            })}
                        >
                            <Element className="actions-button flex items-center text-[#2F3133]">
                                <IconButton
                                    title="Quay lại"
                                    icon="new-arr-left-regular"
                                    onClick={handleBack}
                                    className="text-[20px]  mr-[10px] flex"
                                />
                                {historyUrl.current.length > 1 && (
                                    <IconButton
                                        title="Đóng"
                                        icon="new-x-close-regular"
                                        onClick={handleClose}
                                        className="text-[20px] mr-[10px] flex"
                                    />
                                )}
                            </Element>

                            {isFetching ? (
                                <Skeleton width={100} height={24} />
                            ) : content?.publisher ? (
                                <Element className={styles['publisher-action']}>
                                    <RouterLink to={content.publisher.url}>
                                        <ContentPublisher
                                            publisher={content.publisher}
                                            hStyle={24}
                                            mhStyle={16}
                                        />
                                    </RouterLink>
                                    <FollowButton
                                        objectType={OBJECT_TYPE.PUBLISHER}
                                        objectId={content.publisher.id}
                                        className={cn(
                                            'w-[28px] h-[18px]',
                                            'bg-cover bg-no-repeat',
                                        )}
                                        icon="new-plus-solid"
                                        iconFollow="new-check-circle-solid"
                                        classIcon={cn(
                                            'flex justify-center',
                                            'text-[18px] text-[#ccc]',
                                            'ml-[8px]',
                                            'box-content',
                                        )}
                                        classIconFollow={cn(
                                            'flex justify-center',
                                            'text-[18px] text-[var(--primary)]',
                                            'ml-[8px]',
                                            'box-content',
                                        )}
                                    />
                                </Element>
                            ) : (
                                <></>
                            )}
                            <Element>
                                <ShareButton
                                    title={content?.title}
                                    url={content?.shareUrl}
                                />
                                {content && <ContentMoreAction {...content} />}
                            </Element>
                        </Element>

                        <Element>
                            <Scrollbars
                                id={CONTENT_SCROLL}
                                autoHide
                                autoHideTimeout={1000}
                                autoHideDuration={200}
                            >
                                {content?.ad?.masthead && (
                                    <AdMastheadPopup
                                        content={content}
                                        ad={content.ad}
                                    />
                                )}
                                <GATracking content={content} />
                                <ContentDetailContext.Provider
                                    value={{ register, release }}
                                >
                                    {renderContent()}
                                </ContentDetailContext.Provider>
                            </Scrollbars>
                        </Element>
                    </>
                )}
            </Element>
        </CSSTransition>
    );
}

const EmbedUrl = ({ url }: { url: string }) => {
    const iframeRef = useRef<any>(null);
    const [height, setHeight] = useState<number>(0);

    useEffect(() => {
        if (!iframeRef.current) {
            return;
        }
        const setHeightIframe = (e: any) => {
            const eventName = e.data[0];
            var data = e.data[1];

            switch (eventName) {
                case 'setHeight':
                    setHeight(data);
                    break;
            }
        };
        const layout = document.getElementById('layout');
        layout?.classList.add(styles['embed-layout']);
        window.addEventListener('message', setHeightIframe, false);
        return () => {
            window.removeEventListener('message', setHeightIframe, false);
        };
    }, [iframeRef]);

    return (
        <iframe
            ref={iframeRef}
            id="iframe"
            style={{
                display: 'block',
                position: 'relative',
                width: '100%',
                height: height === 0 ? '100%' : height,
            }}
            src={url}
            title=""
            frameBorder="0"
        />
    );
};
export default memo(ContentUI);
